/**
 * Billing for the open workspace (docs/features/billing.md, the approved billing design): Settings → Billing, the plan
 * picker (annual/monthly, storage add-ons, then Stripe Checkout), the "Confirming your payment" wait after Checkout,
 * the Stripe portal, and the one billing banner across the top of the app. One instance per workspace, shared by the
 * Settings page, the app chrome, the composer (read-only) and the invite sheet (member limit). The view itself is the
 * pure `logic/billing#billingView` / `chromeBanner` / `pickerQuote`, so native ports read the same vectors.
 *
 * `sells` is false on iOS and Android: they never show prices or purchase buttons (their store section is separate). A
 * store-billed workspace (`storeHolding`) doesn't sell on web and desktop either.
 */
import type { BillingInterval, WorkspaceBilling } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { BANNER_SNOOZE_MS, billingView, chromeBanner, inviteRoom, pickerQuote, storeHolding, type BillingBanner, type BillingView, type InviteRoom, type PickerQuote } from "../logic/billing.ts";
import { billingFault, MAX_STORAGE_ADDONS, STORAGE_SNOOZE_MS, storageBanner, storagePrompt, type StorageBanner, type StoragePrompt } from "../logic/storageLimit.ts";
import { ApiFailure, type BillingApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Poll getBilling this often after Checkout until the webhook has landed, at most `CONFIRM_ATTEMPTS` times. */
export const CONFIRM_POLL_MS = 2_000;
export const CONFIRM_ATTEMPTS = 30;

export interface BillingDeps {
  api: BillingApi;
  /** Opens a Stripe-hosted page (same tab on web; the system browser on desktop). */
  open: (url: string) => void;
  /** Whether this client may sell (web and desktop: yes; iOS and Android: never). */
  sells: boolean;
  /** Where a dismissed trial banner's snooze is kept (per workspace); absent ⇒ memory only. */
  snooze?: { get: () => number | undefined; set: (until: number) => void };
  /** Where a dismissed 80 % storage banner's snooze is kept (per workspace); absent ⇒ memory only. */
  storageSnooze?: { get: () => number | undefined; set: (until: number) => void };
}

export type BillingPicker = {
  open: boolean;
  interval: BillingInterval;
  /** Storage add-ons (0–4 × 50 GB). */
  addons: number;
  quote: PickerQuote;
  /** Creating the Checkout session. */
  busy: boolean;
  error?: MessageDescriptor;
};

export type BillingScreenState = {
  status: "loading" | "ready" | "error";
  billing?: WorkspaceBilling;
  view?: BillingView;
  /** The store billing the workspace now (core `storeHolding`): web and desktop sell nothing and say where it's managed. */
  store?: "app_store" | "play";
  /** The banner across the top of the app (null: none). */
  banner: BillingBanner | null;
  /** The storage warning banner (logic/storageLimit#storageBanner), only while `banner` is null: one row at a time. */
  storageBanner: StorageBanner | null;
  picker: BillingPicker;
  /** Back from Checkout: waiting for Stripe's webhook to mark the subscription. */
  confirming: boolean;
  /** The subscription is confirmed: show the "You're on Starter" toast once. */
  confirmed: boolean;
  /** Opening the Stripe portal. */
  portalBusy: boolean;
  notice?: MessageDescriptor;
  /** An upload came back `plan_limit_storage`: the storage-full prompt (logic/storageLimit), until dismissed. */
  storageFull?: StoragePrompt;
};

/** A subscription exists and is paid up (or in a Stripe trial with a card): what Checkout's return waits for. */
export function subscribed(b: WorkspaceBilling): boolean {
  return b.status === "active" || (b.status === "trialing" && b.currentPeriodEnd !== undefined);
}

export class BillingViewModel extends ViewModel<BillingScreenState> {
  private readonly ws: WorkspaceViewModel;
  private readonly deps: BillingDeps;
  private snoozedUntil: number | undefined;
  private storageSnoozedUntil: number | undefined;
  private cancelPoll: (() => void) | undefined;

  constructor(ws: WorkspaceViewModel, deps: BillingDeps) {
    super({
      status: "loading",
      banner: null,
      storageBanner: null,
      picker: { open: false, interval: "year", addons: 0, quote: pickerQuote({ plan: "starter", status: "trialing" }, "year", 0, ws.deps.clock.now()), busy: false },
      confirming: false,
      confirmed: false,
      portalBusy: false,
    });
    this.ws = ws;
    this.deps = deps;
    this.snoozedUntil = deps.snooze?.get();
    this.storageSnoozedUntil = deps.storageSnooze?.get();
    this.onDispose(() => this.cancelPoll?.());
    void this.refresh();
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  private apply(billing: WorkspaceBilling): void {
    const now = this.now();
    const p = this.state.picker;
    const store = storeHolding(billing, now);
    const sells = this.deps.sells && store === undefined;
    const banner = chromeBanner(billing, now, sells, this.snoozedUntil);
    this.update({
      status: "ready",
      billing,
      store,
      view: billingView(billing, now, sells),
      banner,
      storageBanner: banner === null ? storageBanner(billing, now, sells, this.storageSnoozedUntil) : null,
      picker: { ...p, quote: pickerQuote(billing, p.interval, p.addons, now) },
      ...(this.state.storageFull === undefined ? {} : { storageFull: storagePrompt(billing, sells) }),
    });
  }

  async refresh(): Promise<void> {
    try {
      this.apply(await this.deps.api.billing(this.ws.wsId));
    } catch {
      if (this.state.billing === undefined) this.update({ status: "error" });
    }
  }

  /** The invite sheet's limit prompt for this many new invitations (null: they fit, or not loaded yet). */
  inviteRoom(entering: number): InviteRoom | null {
    const b = this.state.billing;
    return b === undefined ? null : inviteRoom(b, entering);
  }

  // ---------- Plan picker → Checkout ----------

  openPicker(): void {
    if (this.state.view?.manage !== true) return;
    const p = this.state.picker;
    this.update({ picker: { open: true, interval: p.interval, addons: p.addons, quote: p.quote, busy: false } });
  }

  closePicker(): void {
    this.update({ picker: { ...this.state.picker, open: false } });
  }

  setInterval(interval: BillingInterval): void {
    this.setPicker(interval, this.state.picker.addons);
  }

  setAddons(addons: number): void {
    this.setPicker(this.state.picker.interval, Math.max(0, Math.min(4, Math.round(addons))));
  }

  private setPicker(interval: BillingInterval, addons: number): void {
    const b = this.state.billing;
    const quote = pickerQuote(b ?? { plan: "starter", status: "trialing" }, interval, addons, this.now());
    this.update({ picker: { ...this.state.picker, interval, addons, quote } });
  }

  /** Creates the Checkout session and opens it; the page comes back with ?checkout=success|canceled. */
  async checkout(): Promise<void> {
    const p = this.state.picker;
    if (p.busy || this.state.view?.manage !== true) return;
    this.update({ picker: { ...p, busy: true } });
    try {
      const url = await this.deps.api.checkout(this.ws.wsId, { interval: p.interval, ...(p.addons > 0 ? { storageAddons: p.addons } : {}) });
      this.deps.open(url);
    } catch (e) {
      this.update({ picker: { ...this.state.picker, busy: false, error: this.errorFor(e) } });
    }
  }

  /** Back from Stripe Checkout. Success: poll until the webhook has landed, then the toast. Canceled: nothing. */
  returnedFromCheckout(result: "success" | "canceled"): void {
    this.closePicker();
    if (result !== "success") return;
    this.update({ confirming: true, confirmed: false });
    this.poll(0);
  }

  private poll(attempt: number): void {
    this.cancelPoll?.();
    this.cancelPoll = undefined;
    void (async () => {
      try {
        const b = await this.deps.api.billing(this.ws.wsId);
        if (this.isDisposed) return;
        this.apply(b);
        if (subscribed(b)) {
          this.update({ confirming: false, confirmed: true });
          return;
        }
      } catch {
        // keep trying
      }
      if (this.isDisposed) return;
      if (attempt + 1 >= CONFIRM_ATTEMPTS) {
        this.update({ confirming: false, notice: msg("billing.confirm.slow") });
        return;
      }
      this.cancelPoll = this.ws.deps.clock.setTimer(CONFIRM_POLL_MS, () => this.poll(attempt + 1));
    })();
  }

  dismissConfirmed(): void {
    this.update({ confirmed: false });
  }

  // ---------- Portal ----------

  /** "Manage billing", "Update payment method", "View invoices", "Switch to annual": Stripe's portal. */
  async portal(): Promise<void> {
    if (this.state.portalBusy || this.state.view?.manage !== true) return;
    this.update({ portalBusy: true });
    try {
      this.deps.open(await this.deps.api.portal(this.ws.wsId));
      this.update({ portalBusy: false });
    } catch (e) {
      this.update({ portalBusy: false, notice: this.errorFor(e) });
    }
  }

  // ---------- Banner and faults ----------

  /** Hides a dismissible trial banner for a day. */
  dismissBanner(): void {
    if (this.state.banner?.kind !== "trial" || !this.state.banner.dismissible) return;
    this.snoozedUntil = this.now() + BANNER_SNOOZE_MS;
    this.deps.snooze?.set(this.snoozedUntil);
    this.update({ banner: null });
  }

  /** Hides the 80 % storage banner for a day (95 % and full can't be dismissed). */
  dismissStorageBanner(): void {
    if (this.state.storageBanner?.dismissible !== true) return;
    this.storageSnoozedUntil = this.now() + STORAGE_SNOOZE_MS;
    this.deps.storageSnooze?.set(this.storageSnoozedUntil);
    this.update({ storageBanner: null });
  }

  /**
   * The storage banner's button: "Add storage" (the portal) or "Choose a plan" (the picker with one more add-on), like
   * the storage-full prompt. "Contact us" is the caller's (it opens a page).
   */
  storageBannerAction(): void {
    const action = this.state.storageBanner?.action;
    if (action === "addStorage") void this.portal();
    else if (action === "choosePlan") {
      this.setAddons(Math.min(MAX_STORAGE_ADDONS, (this.state.billing?.storageAddons ?? 0) + 1));
      this.openPicker();
    }
  }

  /**
   * A write, invite or upload came back `billing_required` / `plan_limit_members` / `plan_limit_storage`: the plan
   * changed under us, so re-read it. A full storage pool also opens the storage-full prompt.
   */
  noteFault(code: string | undefined): void {
    const fault = billingFault(code);
    if (fault === null) return;
    if (fault === "plan_limit_storage") this.update({ storageFull: storagePrompt(this.state.billing, this.deps.sells && this.state.store === undefined) });
    void this.refresh();
  }

  dismissStorageFull(): void {
    if (this.state.storageFull === undefined) return;
    const s = { ...this.state };
    delete s.storageFull;
    this.setState(s);
  }

  /**
   * The storage-full prompt's button: "Add storage" opens the portal (it changes the add-on quantity), "Choose a plan"
   * opens the picker with one more add-on than now. "Contact us" is the caller's (it opens a page).
   */
  addStorage(): void {
    const action = this.state.storageFull?.action;
    if (action !== "addStorage" && action !== "choosePlan") return;
    this.dismissStorageFull();
    if (action === "addStorage") {
      void this.portal();
      return;
    }
    this.setAddons(Math.min(MAX_STORAGE_ADDONS, (this.state.billing?.storageAddons ?? 0) + 1));
    this.openPicker();
  }

  dismissNotice(): void {
    const s = { ...this.state };
    delete s.notice;
    this.setState(s);
  }

  private errorFor(e: unknown): MessageDescriptor {
    if (e instanceof ApiFailure && e.code === "billing_unavailable") return msg("billing.error.unavailable");
    if (e instanceof ApiFailure && e.kind === "network") return msg("billing.error.network");
    return msg("billing.error.generic");
  }
}
